// Deterministic character picker for the Forever Wall.
//
// Every donation is bound to one Kenney 3D character (a GLB asset under
// /overlay-demo/assets/{cube-pets,mini-characters}/). The character is
// picked from a stable hash of the donor's handle (or, for anonymous
// drops, the typed display name), so a returning supporter always shows
// up as the same critter on every wall — that's the "identity" hook for
// repeat donors.
//
// The manifest is fetched once and cached for the lifetime of the page.

export interface CharacterPick {
  id: string;            // short id, e.g. "animal-fox" (used as DB key)
  path: string;          // public path, e.g. "/overlay-demo/assets/cube-pets/animal-fox.glb"
  label: string;         // human label for UI, e.g. "Fox"
  group: "cube-pets" | "mini-characters";
}

let manifestCache: string[] | null = null;
let manifestPromise: Promise<string[]> | null = null;

const MANIFEST_URL = "/overlay-demo/manifest.json";

// Embedded fallback so tests / preview without network still pick something
// reasonable. Kept in sync with public/overlay-demo/manifest.json.
const FALLBACK_MANIFEST: string[] = [
  "assets/cube-pets/animal-beaver.glb",
  "assets/cube-pets/animal-bee.glb",
  "assets/cube-pets/animal-bunny.glb",
  "assets/cube-pets/animal-cat.glb",
  "assets/cube-pets/animal-dog.glb",
  "assets/cube-pets/animal-fox.glb",
  "assets/cube-pets/animal-lion.glb",
  "assets/cube-pets/animal-panda.glb",
  "assets/cube-pets/animal-penguin.glb",
  "assets/cube-pets/animal-tiger.glb",
  "assets/mini-characters/character-female-a.glb",
  "assets/mini-characters/character-female-b.glb",
  "assets/mini-characters/character-female-c.glb",
  "assets/mini-characters/character-male-a.glb",
  "assets/mini-characters/character-male-b.glb",
  "assets/mini-characters/character-male-c.glb",
];

async function loadManifest(): Promise<string[]> {
  if (manifestCache) return manifestCache;
  if (manifestPromise) return manifestPromise;
  manifestPromise = (async () => {
    try {
      const res = await fetch(MANIFEST_URL, { credentials: "omit" });
      if (!res.ok) throw new Error(String(res.status));
      const data = (await res.json()) as string[];
      if (!Array.isArray(data) || data.length === 0) throw new Error("empty");
      manifestCache = data;
      return data;
    } catch {
      manifestCache = FALLBACK_MANIFEST;
      return FALLBACK_MANIFEST;
    }
  })();
  return manifestPromise;
}

// FNV-1a 32-bit. Tiny, deterministic, fine for "pick one of ~40 buckets".
// Same impl on client + (future) server gives identical results.
export function hashHandle(input: string): number {
  const norm = (input || "anon").trim().toLowerCase().replace(/^@/, "") || "anon";
  let h = 0x811c9dc5;
  for (let i = 0; i < norm.length; i++) {
    h ^= norm.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function entryToPick(entry: string): CharacterPick {
  // entry looks like "assets/cube-pets/animal-fox.glb"
  const parts = entry.split("/");
  const file = parts[parts.length - 1] ?? entry;
  const id = file.replace(/\.glb$/i, "");
  const group: CharacterPick["group"] =
    entry.includes("mini-characters") ? "mini-characters" : "cube-pets";
  const label = id
    .replace(/^animal-/, "")
    .replace(/^character-/, "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return {
    id,
    path: "/overlay-demo/" + entry.replace(/^\/+/, ""),
    label,
    group,
  };
}

export async function pickCharacterForHandle(handle: string): Promise<CharacterPick> {
  const list = await loadManifest();
  const idx = hashHandle(handle) % list.length;
  return entryToPick(list[idx]!);
}

// Synchronous variant used before the manifest has loaded; uses the fallback.
export function pickCharacterForHandleSync(handle: string): CharacterPick {
  const list = manifestCache ?? FALLBACK_MANIFEST;
  const idx = hashHandle(handle) % list.length;
  return entryToPick(list[idx]!);
}
